import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { MenuItem } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UserContext } from '../../contexts/User';
import useWorkspaces from '../../hooks/useWorkspaces';
import WorkspaceSVG from '../../images/workspace.svg';
import { Dropdown } from '../muiCustomized/Dropdown';
import './sidebar.css';
import SubMenu from './submenu';
import { canAccessNavItem, getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';

const Avatar = styled.div`
  border-radius: 6px;
  width: 39px;
  height: 39px;
  background: var(--dark-7);
  color: var(--dark-3);
  ${(props) => props.src && `
    background-size: cover;
    background-image: url(${props.src});
  `}
`;

const linkArrayNamesAdmin = [
  {
    routeName: '/dashboard',
    text: 'Dashboard',
    enabled: true,
    permission: 'dashboard:read',
    accountLevel: 1,
  },
  {
    text: 'Team Engagement',
    routeName: '/members/team-engagement',
    enabled: true,
    permission: 'team:read',
    accountLevel: 2,
  },
  {
    text: 'Profile Update Requests',
    routeName: '/profile-update-requests',
    enabled: true,
    permission: 'profile:review',
    accountLevel: 2,
  },
  {
    text: 'Organization Chart',
    routeName: '/organization-chart',
    enabled: true,
    permission: 'organization:read',
    accountLevel: 2,
  },
  {
    text: 'Analytics',
    enabled: true,
    submenuLocation: '/analytics',
    accountLevel: 2,
    subRoutes: [
      { text: 'Unusual Activity', routeName: '/analytics/unusual-activity', enabled: true },
    ],
  },
  {
    text: 'Reports',
    enabled: true,
    submenuLocation: '/reports',
    accountLevel: 2,
    permission: 'report:read',
    subRoutes: [
      {
        text: 'Time Worked',
        routeName: '/reports/time-worked',
        enabled: true,
        permission: 'report:read',
        accountLevel: 2,
      },
      {
        text: 'Webcam Audit Results',
        routeName: '/reports/webcam-results',
        enabled: true,
        permission: 'audit:webcam',
        accountLevel: 2,
      },
      {
        routeName: '/insiderThreats',
        text: 'Insider Threats',
        enabled: true,
        permission: 'dashboard:insider-threats',
        accountLevel: 2,
      },
      {
        text: 'Timesheets',
        routeName: '/members/timesheets',
        enabled: true,
        permission: 'timesheet:read:all',
        accountLevel: 2,
      },
      {
        text: 'Geolocation Results',
        routeName: '/reports/geo-results',
        enabled: true,
        permission: 'audit:geolocation',
        accountLevel: 2,
      },
      {
        text: "Team Member's Time Off",
        routeName: '/pto/members',
        enabled: true,
        permission: 'pto:read:team',
        accountLevel: 2,
      },
    ],
  },
  {
    routeName: '/geoDashboard',
    text: 'Geo Dashboard',
    enabled: true,
    permission: 'dashboard:geo',
    accountLevel: 2,
  },
  {
    text: 'Geo Map',
    routeName: '/geoMap',
    enabled: true,
    permission: 'dashboard:geomap',
    accountLevel: 2,
  },
  {
    text: 'My Shoot',
    submenuLocation: '/pto',
    subRoutes: [
      {
        text: 'Personal Time Off (PTO)',
        routeName: '/pto/requests',
        enabled: true,
        permission: 'pto:create:own',
        accountLevel: 1,
      },
    ],
    enabled: true,
    permission: 'pto:read:own',
    accountLevel: 1,
  },
  {
    text: 'Manage Members',
    routeName: '/members/manage',
    enabled: true,
    permission: 'member:manage',
    accountLevel: 2,
  },
  // {
  //   text: 'Manage Members',
  //   // Used within the submenu component to look at the user's path and keep the menu open
  //   submenuLocation: '/members',
  //   accountLevel: 2,
  //   subRoutes: [
  //     { text: 'Members', routeName: '/members/manage', enabled: true },
  //     // { text: 'Screenshots', routeName: '/members/screenshotFrequency', enabled: true },
  //     // { text: 'Webcam Audit', routeName: '/members/webcamAudit', enabled: true },
  //     // { text: 'Activity', routeName: '/members/activity', enabled: true },
  //     // { text: 'Data Retention', routeName: '/members/dataRetention' },
  //     // { text: 'Messaging', routeName: '/members/messaging' },
  //   ],
  //   enabled: true,
  // },
  {
    text: 'Settings & Permissions',
    // Used within the submenu component to look at the user's path and keep the menu open
    submenuLocation: '/settings',
    accountLevel: 2,
    permission: 'settings:read',
    subRoutes: [
      {
        text: 'Screenshots',
        routeName: '/members/screenshotFrequency',
        enabled: true,
        permission: 'settings:screenshots',
        accountLevel: 2,
      },
      {
        text: 'Webcam Audit',
        routeName: '/members/webcamAudit',
        enabled: true,
        permission: 'settings:webcam',
        accountLevel: 2,
      },
      {
        text: 'Team Management',
        routeName: '/admin/team-settings',
        enabled: true,
        permission: 'settings:team',
        accountLevel: 2,
      },
      {
        text: 'All Settings',
        routeName: '/admin/settings',
        enabled: true,
        permission: 'settings:update:workspace',
        accountLevel: 2,
      },
      // { text: 'Activity', routeName: '/members/activity', enabled: true },
      // { text: 'Data Retention', routeName: '/members/dataRetention' },
      // { text: 'Messaging', routeName: '/members/messaging' },
    ],
    enabled: true,
  },
  {
    routeName: '/role-management',
    text: 'Role Management',
    enabled: true,
    accountLevel: 3,
    permission: 'workspace:manage',
  },
  {
    routeName: '/activity-logs',
    text: 'Activity Logs',
    enabled: true,
    accountLevel: 3,
    permission: 'activity:read:all',
  },
  // {
  //   routeName: '/devices',
  //   text: 'Devices Dashboard',
  // },
  // {
  //   routeName: '/complianceDashboard',
  //   text: 'Compliance Dashboard',
  // },
  // {
  //   routeName: '/itAssistanceRequests',
  //   text: 'IT Assistance Requests',
  // },
  // {
  //   routeName: '/widgets',
  //   text: 'Widgets',
  // },
  // {
  //   routeName: '/analytics',
  //   text: 'Analytics',
  // },
  // {
  //   routeName: '/documentation',
  //   text: 'Documentation',
  // },
];

const linkArrayNamesTeamLeader = [
  {
    routeName: '/dashboard',
    text: 'Dashboard',
    enabled: true,
    permission: 'dashboard:read',
    accountLevel: 2,
  },
  {
    text: 'Team Engagement',
    routeName: '/members/team-engagement',
    enabled: true,
    permission: 'team:read',
    accountLevel: 2,
  },
  {
    text: 'Profile Update Requests',
    routeName: '/profile-update-requests',
    enabled: true,
    permission: 'profile:review',
    accountLevel: 2,
  },
  {
    text: 'Reports',
    enabled: true,
    submenuLocation: '/reports',
    accountLevel: 2,
    permission: 'report:read',
    subRoutes: [
      {
        text: 'Timesheets',
        routeName: '/members/timesheets',
        enabled: true,
        permission: 'timesheet:read:team',
        accountLevel: 2,
      },
    ],
  },
  {
    text: 'Time Off Requests',
    submenuLocation: '/pto',
    permission: 'pto:read:own',
    accountLevel: 1,
    subRoutes: [
      {
        text: 'Personal Time Off (PTO)',
        routeName: '/pto/requests',
        enabled: true,
        permission: 'pto:create:own',
        accountLevel: 1,
      },
      {
        text: "Team Member's Time Off",
        routeName: '/pto/members',
        enabled: true,
        permission: 'pto:read:team',
        accountLevel: 2,
      },
    ],
    enabled: true,
  },
];

const linkArrayNamesMember = [
  {
    routeName: '/pto/requests',
    text: 'Time Off Requests',
    enabled: true,
    permission: 'pto:create:own',
    accountLevel: 1,
  },
  // {
  //   text: 'Reports',
  //   enabled: true,
  //   submenuLocation: '/reports',
  //   accountLevel: 2,
  //   subRoutes: [
  //     { text: 'Timesheets', routeName: '/members/timesheets', enabled: true },
  //   ],
  // },
];

const NoOtherWorkspacesText = styled.p`
  margin: 0 11px;
  font-size: 15px;
  white-space: nowrap;
  color: var(--dark-4);
`;

const MenuLink = styled.li`
  ${(props) => !props.enabled && css`
  cursor: disabled;
  pointer-events: none;
  opacity: 0.3;
  `};
`;

function Sidebar({ children }) {
  const {
    activeWorkspace, workspaces, member,
  } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const { handleWorkspaceSelection } = useWorkspaces();

  if (member.isReportingManager && !linkArrayNamesMember.some((link) => link.routeName === '/dashboard')) {
    linkArrayNamesMember.unshift({
      routeName: '/dashboard',
      text: 'Dashboard',
      enabled: true,
      permission: 'workspace:read',
    });
  }
  // eslint-disable-next-line no-nested-ternary
  const memberLevel = getMemberRoleLevel(member);
  // eslint-disable-next-line no-nested-ternary
  const linkArray = memberLevel >= accountTypeLevel['Workspace admin']
    ? linkArrayNamesAdmin
    : (memberLevel === accountTypeLevel['Team Leader'] || member.isReportingManager)
      ? linkArrayNamesTeamLeader : linkArrayNamesMember;

  // Filter nav items based on permissions
  const filteredLinkArray = linkArray.filter((linkInfo) => canAccessNavItem(linkInfo, member));

  return (
    <>
      <div className="sidenav">
        <div className="user-menu-sidenav">
          <div className="user-menu-avatar">
            <Avatar className="flex-row align-c jstfy-c" src={activeWorkspace.picture || WorkspaceSVG} />
          </div>
          <div>
            <div className="content">
              <h3>Current Workspace</h3>
              <Dropdown
                displayEmpty
                renderValue={() => activeWorkspace?.name}
                setValue={() => { }}
              >
                {workspaces?.length > 1
                  ? workspaces
                    .filter((workspace) => workspace.id !== activeWorkspace?.id)
                    .map((workspace) => (
                      <MenuItem
                        key={workspace.name}
                        onClick={() => handleWorkspaceSelection(workspace)}
                      >
                        {workspace.name}
                      </MenuItem>
                    ))
                  : <NoOtherWorkspacesText>No other workspaces</NoOtherWorkspacesText>}
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="nav-contents">
          { memberLevel >= accountTypeLevel['Workspace admin']
            && (
            <h3>
              {' '}
              ADMINISTRATION
                {' '}
            </h3>
            )}
          <ul className="admin-nav">
            { filteredLinkArray.map((linkInfo) => (linkInfo?.subRoutes
              ? (
                <SubMenu
                  key={linkInfo.text}
                  linkInfo={linkInfo}
                  open={open}
                  setOpen={(info) => setOpen(info)}
                  member={member}
                />
              )
              : (
                <MenuLink
                  key={linkInfo.routeName}
                  enabled={linkInfo.enabled}
                  className="link-item"
                >
                  <NavLink
                    to={linkInfo.routeName}
                    activeClassName="active-link"
                    className="link-text"
                  >
                    {linkInfo.text}
                  </NavLink>
                </MenuLink>
              )))}
          </ul>
        </div>
      </div>
      {children}
    </>
  );
}

export { MenuLink, Sidebar };

Sidebar.propTypes = {
  children: PropTypes.elementType,
};
